import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

/**
 * Agent Event Types for categorizing different kinds of agent activities
 */
export type AgentEventType = 
  | 'thinking'      // Agent is reasoning/processing
  | 'action'        // Agent is taking an action
  | 'result'        // Agent produced a result
  | 'error'         // An error occurred
  | 'document'      // Document processing event
  | 'calculation'   // Income calculation event
  | 'phase_start'   // Workflow phase started
  | 'phase_complete' // Workflow phase completed
  | 'progress'      // Progress update

/**
 * Individual agent event
 */
export interface AgentEvent {
  id: string
  timestamp: number
  type: AgentEventType
  phase: string
  message: string
  details?: Record<string, unknown>
  progress?: number
  duration?: number // milliseconds
}

/**
 * Current agent action state
 */
export interface CurrentAction {
  action: string
  startedAt: number
  phase: string
  progress: number
}

/**
 * Agent events state for a single case
 */
export interface CaseAgentState {
  events: AgentEvent[]
  currentAction: CurrentAction | null
  isProcessing: boolean
  lastEventAt: number | null
}

/**
 * Root state for agent events slice
 */
export interface AgentEventsState {
  byCaseId: Record<string, CaseAgentState>
}

const MAX_EVENTS_PER_CASE = 100

const initialCaseState: CaseAgentState = {
  events: [],
  currentAction: null,
  isProcessing: false,
  lastEventAt: null,
}

const initialState: AgentEventsState = {
  byCaseId: {},
}

const agentEventsSlice = createSlice({
  name: 'agentEvents',
  initialState,
  reducers: {
    /**
     * Add a new agent event for a case
     */
    addAgentEvent(
      state,
      action: PayloadAction<{ caseId: string; event: AgentEvent }>
    ) {
      const { caseId, event } = action.payload

      if (!state.byCaseId[caseId]) {
        state.byCaseId[caseId] = { ...initialCaseState, events: [] }
      }

      const caseState = state.byCaseId[caseId]

      // Avoid duplicate events (same message within 1 second)
      const lastEvent = caseState.events[caseState.events.length - 1]
      if (
        lastEvent &&
        lastEvent.message === event.message &&
        event.timestamp - lastEvent.timestamp < 1000
      ) {
        return
      }

      // Add event and trim to max
      caseState.events.push(event)
      if (caseState.events.length > MAX_EVENTS_PER_CASE) {
        caseState.events = caseState.events.slice(-MAX_EVENTS_PER_CASE)
      }

      caseState.lastEventAt = event.timestamp

      // Update processing state based on event type
      if (event.type === 'phase_start' || event.type === 'action' || event.type === 'thinking') {
        caseState.isProcessing = true
        caseState.currentAction = {
          action: event.message,
          startedAt: event.timestamp,
          phase: event.phase,
          progress: event.progress || 0,
        }
      } else if (event.type === 'phase_complete' || event.type === 'result') {
        // Keep processing if there's progress < 100
        if (event.progress !== undefined && event.progress >= 100) {
          caseState.isProcessing = false
          caseState.currentAction = null
        }
      } else if (event.type === 'error') {
        caseState.isProcessing = false
        caseState.currentAction = null
      }
    },

    /**
     * Add multiple events at once (for batch updates)
     */
    addAgentEvents(
      state,
      action: PayloadAction<{ caseId: string; events: AgentEvent[] }>
    ) {
      const { caseId, events } = action.payload

      if (!state.byCaseId[caseId]) {
        state.byCaseId[caseId] = { ...initialCaseState, events: [] }
      }

      const caseState = state.byCaseId[caseId]

      for (const event of events) {
        // Avoid duplicates
        const lastEvent = caseState.events[caseState.events.length - 1]
        if (
          lastEvent &&
          lastEvent.message === event.message &&
          event.timestamp - lastEvent.timestamp < 1000
        ) {
          continue
        }

        caseState.events.push(event)
      }

      // Trim to max
      if (caseState.events.length > MAX_EVENTS_PER_CASE) {
        caseState.events = caseState.events.slice(-MAX_EVENTS_PER_CASE)
      }

      const lastEvent = events[events.length - 1]
      if (lastEvent) {
        caseState.lastEventAt = lastEvent.timestamp
      }
    },

    /**
     * Update current action progress
     */
    updateCurrentAction(
      state,
      action: PayloadAction<{
        caseId: string
        action: string
        phase: string
        progress: number
      }>
    ) {
      const { caseId, action: actionName, phase, progress } = action.payload

      if (!state.byCaseId[caseId]) {
        state.byCaseId[caseId] = { ...initialCaseState, events: [] }
      }

      const caseState = state.byCaseId[caseId]
      caseState.isProcessing = true
      caseState.currentAction = {
        action: actionName,
        startedAt: caseState.currentAction?.startedAt || Date.now(),
        phase,
        progress,
      }
    },

    /**
     * Set processing state for a case
     */
    setProcessing(
      state,
      action: PayloadAction<{ caseId: string; isProcessing: boolean }>
    ) {
      const { caseId, isProcessing } = action.payload

      if (!state.byCaseId[caseId]) {
        state.byCaseId[caseId] = { ...initialCaseState, events: [] }
      }

      state.byCaseId[caseId].isProcessing = isProcessing
      if (!isProcessing) {
        state.byCaseId[caseId].currentAction = null
      }
    },

    /**
     * Clear all events for a case
     */
    clearCaseEvents(state, action: PayloadAction<string>) {
      const caseId = action.payload
      if (state.byCaseId[caseId]) {
        state.byCaseId[caseId] = { ...initialCaseState, events: [] }
      }
    },

    /**
     * Clear events older than a certain timestamp
     */
    pruneOldEvents(
      state,
      action: PayloadAction<{ caseId: string; olderThan: number }>
    ) {
      const { caseId, olderThan } = action.payload
      const caseState = state.byCaseId[caseId]

      if (caseState) {
        caseState.events = caseState.events.filter(
          (event) => event.timestamp > olderThan
        )
      }
    },
  },
})

export const {
  addAgentEvent,
  addAgentEvents,
  updateCurrentAction,
  setProcessing,
  clearCaseEvents,
  pruneOldEvents,
} = agentEventsSlice.actions

export default agentEventsSlice.reducer

// =============================================================================
// SELECTORS
// =============================================================================

import type { RootState } from '../index'

/**
 * Select all agent events for a case
 */
export const selectAgentEvents = (caseId: string) => (state: RootState) =>
  state.agentEvents?.byCaseId[caseId]?.events || []

/**
 * Select the most recent N events for a case
 */
export const selectRecentAgentEvents =
  (caseId: string, count: number = 10) =>
  (state: RootState) => {
    const events = state.agentEvents?.byCaseId[caseId]?.events || []
    return events.slice(-count)
  }

/**
 * Select current action for a case
 */
export const selectCurrentAction = (caseId: string) => (state: RootState) =>
  state.agentEvents?.byCaseId[caseId]?.currentAction || null

/**
 * Select whether agent is processing for a case
 */
export const selectIsAgentProcessing = (caseId: string) => (state: RootState) =>
  state.agentEvents?.byCaseId[caseId]?.isProcessing || false

/**
 * Select last event timestamp for a case
 */
export const selectLastEventAt = (caseId: string) => (state: RootState) =>
  state.agentEvents?.byCaseId[caseId]?.lastEventAt || null

/**
 * Select events grouped by phase
 */
export const selectEventsByPhase = (caseId: string) => (state: RootState) => {
  const events = state.agentEvents?.byCaseId[caseId]?.events || []
  const grouped: Record<string, AgentEvent[]> = {}

  for (const event of events) {
    const phase = event.phase || 'unknown'
    if (!grouped[phase]) {
      grouped[phase] = []
    }
    grouped[phase].push(event)
  }

  return grouped
}

/**
 * Select the latest event for a case
 */
export const selectLatestEvent = (caseId: string) => (state: RootState) => {
  const events = state.agentEvents?.byCaseId[caseId]?.events || []
  return events.length > 0 ? events[events.length - 1] : null
}

/**
 * Select full case agent state
 */
export const selectCaseAgentState = (caseId: string) => (state: RootState) =>
  state.agentEvents?.byCaseId[caseId] || initialCaseState
